import { ComponentRef } from '@angular/core';
import { TemplateContext } from '../../../../models/reportTemplate.model';
import { Sample } from '../../../../models/sample.model';
import { FilterOption } from '../../../../services/template-mappers/base-template-mapper';
import { EditorPropertySchema, EditorRenderStrategy, WidgetPropertyType, parseAttribute } from '../widget-config.helpers';
import { MultiSampleSurvey, MultiSampleTableWidgetComponent } from './multi-sample-table-widget.component';

export enum MultiSampleTableWidgetAttr {
    ShowHeader = 'showHeader',
    ExcludeAdjustedOel = 'excludeAdjustedOel'
}

interface ComputedMultiSampleData extends Record<string, unknown> {
    survey?: MultiSampleSurvey;
    samples?: Sample[];
}

interface MultiSampleContext extends TemplateContext {
    survey?: MultiSampleSurvey;
    samples?: Sample[];
    appliedFilters?: FilterOption[];
    computedData?: ComputedMultiSampleData;
}

export function configureMultiSampleTableWidget(
    componentRef: ComponentRef<unknown>,
    context: TemplateContext,
    sourceElement?: HTMLElement
): void {
    const component = componentRef.instance as MultiSampleTableWidgetComponent;
    const ctx = context as MultiSampleContext;

    const survey = ctx.computedData?.survey ?? ctx.survey;
    const samples = ctx.computedData?.samples ?? ctx.samples ?? [];
    component.data = samples.length ? { survey, samples } : null;
    component.showHeader = parseAttribute(sourceElement?.getAttribute(MultiSampleTableWidgetAttr.ShowHeader), true);
    component.isPdfMode = true;

    const oelFilter = ctx.appliedFilters?.find(f => f.path === 'oelTypeId');
    if (oelFilter) {
        component.selectedOelFilters = oelFilter.selectedValues || [];
    }
}

const cellBase = 'padding:6px 8px;border:1px solid #999;font-size:10px;vertical-align:middle;';
const rowHeaderStyle = `${cellBase}background:#f5f5f5;font-weight:bold;text-align:left;`;
const sampleValueStyle = `${cellBase}text-align:center;`;
const headerCellStyle = `${cellBase}background:#eeeeee;font-weight:bold;text-align:center;`;
const oelColumnStyle = `${cellBase}text-align:center;vertical-align:middle;font-weight:bold;`;
const agentLabelStyle = `${cellBase}background:#f5f5f5;font-weight:bold;text-align:left;`;
const jurisdictionBandStyle = `${cellBase}background:#e8f4f8;font-weight:bold;color:#2c5aa0;text-align:left;`;

// Number of per-sample field rows the OEL type cell spans (Job Title/Area + the 8 fieldRow rows below).
const OEL_FIELD_ROWSPAN = 9;

const fieldRow = (label: string, prop: string) =>
    `<tr><td style="${rowHeaderStyle}">${label}</td>` +
    `<td data-tpl-each="this.sampleColumns" style="${sampleValueStyle}">[[this.${prop}]]</td></tr>`;

export const multiSampleTableLoopTemplate = [
    '[[#if computedData.multiSampleTable.hasData]]',
    '[[#each computedData.multiSampleTable.tableGroups]]',
    '<div style="margin-bottom:20px;page-break-inside:avoid;break-inside:avoid;">',
    '<div style="text-align:right;font-style:italic;color:#666;font-size:11px;margin-bottom:4px;">',
    '[All Concentrations shown with individual units]',
    '</div>',
    '<table style="width:100%;border-collapse:collapse;font-size:11px;color:#000;">',
    '<thead>',
    '<tr>',
    `<th style="${headerCellStyle}">Sample Number #:</th>`,
    `<th data-tpl-each="this.sampleColumns" style="${headerCellStyle}">[[this.sampleNumber]]</th>`,
    `<th data-tpl-if="this.hasOelColumns" data-tpl-attr-colspan="this.oelColumnCount" style="${headerCellStyle}">Occupational Exposure Limits [mg/m³]</th>`,
    '</tr>',
    '</thead>',
    '<tbody>',
    '<tr>',
    `<td style="${rowHeaderStyle}">Job Title/Area:</td>`,
    `<td data-tpl-each="this.sampleColumns" style="${sampleValueStyle}">[[this.segName]]</td>`,
    `<td data-tpl-each="this.oelColumns" rowspan="${OEL_FIELD_ROWSPAN}" data-tpl-style="vertical-align:middle;[[this.headerStyle]]" style="${oelColumnStyle}">[[this.oelTypeName]]</td>`,
    '</tr>',
    fieldRow('Sample Type:', 'sampleType'),
    fieldRow('Resp. Type:', 'respType'),
    fieldRow('Resp. APF:', 'respApf'),
    fieldRow('MUC Exceeded:', 'mucExceeded'),
    fieldRow('Start Date:', 'startDate'),
    fieldRow('Start Time:', 'startTime'),
    fieldRow('Duration (min):', 'duration'),
    fieldRow('Volume (Liters):', 'volume'),
    '<tr data-tpl-if="this.showJurisdictionHeader">',
    `<td colspan="3" data-tpl-attr-colspan="this.totalColspan" style="${jurisdictionBandStyle}">[[this.jurisdictionName]]</td>`,
    '</tr>',
    '<tr data-tpl-each="this.agentRows">',
    `<td style="${agentLabelStyle}">[[this.agentName]]</td>`,
    `<td data-tpl-each="this.concentrationCells" data-tpl-style="[[this.cellStyle]]" style="${sampleValueStyle}">[[this.concentration]]</td>`,
    `<td data-tpl-each="this.oelValueCells" data-tpl-style="[[this.cellStyle]]" style="${oelColumnStyle}">[[this.oelValue]]</td>`,
    '</tr>',
    '</tbody>',
    '</table>',
    '<div style="margin-top:10px;display:flex;flex-wrap:wrap;gap:15px;font-size:10px;">',
    '[[#each this.legendItems]]',
    '<div style="display:inline-flex;align-items:center;gap:3px;font-weight:bold;">',
    '<span data-tpl-style="[[this.swatchStyle]]" style="background:#cccccc;display:inline-block;width:14px;height:14px;border-radius:2px;border:1px solid rgba(0,0,0,0.2);">&#8203;</span>',
    '<span>&gt; [[this.legendText]]</span>',
    '</div>',
    '[[/each]]',
    '</div>',
    '</div>',
    '[[/each]]',
    '<div style="margin-top:20px;">',
    '<table style="width:100%;border-collapse:collapse;font-size:11px;color:#000;">',
    '<tbody>',
    '<tr data-tpl-each="computedData.multiSampleTable.taskAreaRows">',
    '<td style="border:1px solid #999;padding:8px;vertical-align:top;width:28%;">',
    '<div style="font-weight:bold;font-size:10px;white-space:nowrap;margin-bottom:4px;">Sample Number #:</div>',
    '<div style="font-size:10px;">[[this.sampleNumber]]</div>',
    '</td>',
    '<td style="border:1px solid #999;padding:8px;vertical-align:top;">',
    '<div style="font-weight:bold;font-size:10px;margin-bottom:4px;">Task/Area:</div>',
    '<div style="font-size:10px;">[[this.taskDescription]]</div>',
    '</td>',
    '</tr>',
    '</tbody>',
    '</table>',
    '</div>',
    '[[/if]]'
].join('\n');

export const multiSampleLoopScopes: { loopExpr: string; label: string; fields: string[] }[] = [
    { loopExpr: 'this.sampleColumns', label: 'Per Sample', fields: ['sampleNumber', 'segName', 'sampleType', 'respType', 'respApf', 'mucExceeded', 'startDate', 'startTime', 'duration', 'volume'] },
    { loopExpr: 'this.oelColumns', label: 'Per OEL Limit', fields: ['oelTypeName'] },
    { loopExpr: 'this.agentRows', label: 'Per Agent', fields: ['agentName'] },
    { loopExpr: 'this.concentrationCells', label: 'Per Sample Result', fields: ['concentration'] },
    { loopExpr: 'this.oelValueCells', label: 'Per OEL Result', fields: ['oelValue'] },
    { loopExpr: 'this.legendItems', label: 'Per Legend Item', fields: ['legendText'] },
    { loopExpr: 'computedData.multiSampleTable.taskAreaRows', label: 'Per Task/Area Row', fields: ['sampleNumber', 'taskDescription'] },
];

export const multiSampleTableEditorPropertySchema: EditorPropertySchema = {
    renderStrategy: EditorRenderStrategy.ReadOnly,
    propertySchema: [
        { key: MultiSampleTableWidgetAttr.ShowHeader, label: 'Show Header', type: WidgetPropertyType.Boolean },
        { key: MultiSampleTableWidgetAttr.ExcludeAdjustedOel, label: 'Exclude Adjusted OEL', type: WidgetPropertyType.Boolean }
    ]
};
